/**
 * The furniture every reading in the loop shares.
 *
 * A reading is one screen of one stage, and the navigation between readings is
 * the sidebar — the same place every other screen in this product is reached
 * from. A tab strip per stage would have been five second navigations, in a
 * panel whose sidebar is already the diagram of the product.
 *
 * What they do share is the header: the stage they belong to, the reading being
 * looked at, and the scope bar that decides what every number under it means.
 * One component, because a screen that arranged the scope differently is a
 * screen where somebody reads a table against the wrong period — and because
 * the scope now carries across all five stages, where before it silently
 * stopped applying the moment somebody left the detection screens.
 */

import type { ReactNode } from "react";
import { useT } from "~/core/i18n";
import { PageHead } from "~/core/ui/page";
import type { FilterState } from "~/modules/analytics/components/filters";
import { ScopeBar } from "~/modules/analytics/components/filters";
import { READING_ICONS } from "~/modules/analytics/components/readings";
import { CONTENT_PATH, CONTENT_SCOPE, type ReadingSlug, stageOfReading } from "~/modules/analytics/lib/loop";
import { AsideToggle } from "~/shell/aside";

/** What every reading is handed. */
export interface ReadingProps {
	entityId: string;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
}

export function StageShell({
	entityId,
	reading,
	title,
	icon,
	filters,
	setFilters,
	lede,
	topics,
	tags,
	actions,
	children,
}: ReadingProps & {
	/** Which reading this is. Its stage and its scope come from `LOOP`, which
	 *  is also why this is the slug union and not a string: a screen naming a
	 *  reading the table does not have would otherwise draw a bare key. The
	 *  content tree is the one screen outside the table that wears the bar. */
	reading: ReadingSlug | typeof CONTENT_PATH;
	/** Required, in effect, for the content tree, which has no reading's name. */
	title?: string;
	icon?: ReactNode;
	/** What this reading is, in one sentence. The stage's own sentence where a
	 *  reading has nothing more specific to say than the four beside it do. */
	lede?: string;
	/** The topics that exist, where this reading is the one that loaded them. */
	topics?: string[];
	tags?: string[];
	/** This screen's own control, in the head beside the scope. The one filled
	 *  button a screen is allowed, where it has one. */
	actions?: ReactNode;
	children: ReactNode;
}) {
	const t = useT();
	// Always found — `reading` is one of the table's own slugs — but `find`
	// cannot say so, and a fallback is cheaper than an assertion.
	const stage = stageOfReading(reading);
	const scope =
		reading === CONTENT_PATH ? CONTENT_SCOPE : (stage?.readings.find((one) => one.slug === reading)?.scope ?? []);

	// No bar at all for a reading that honours nothing. An empty row of controls
	// is a row that says a scope is being applied here, and none is.
	const bar =
		scope.length > 0 ? (
			<ScopeBar
				entityId={entityId}
				tab={reading}
				segments={scope}
				value={filters}
				onChange={setFilters}
				topics={topics}
				tags={tags}
			/>
		) : undefined;

	return (
		<>
			{/* No stage beside the title. The sidebar already groups every reading
			    under its numbered stage, and repeating it here read as a crumb
			    pointing at a page — "Platforms / Detect" — when Detect is a heading
			    in the navigation and not somewhere anybody can go. Settings keeps
			    its own, where the parent really is a list on the left. */}
			<PageHead
				title={title ?? t(`reading.${reading}`)}
				icon={icon ?? (reading === CONTENT_PATH ? undefined : READING_ICONS[reading])}
				lede={lede ?? t(`stage.${stage?.key ?? "detect"}.lede`)}
				filters={bar}
				actions={
					<>
						{actions}
						<AsideToggle />
					</>
				}
			/>
			{children}
		</>
	);
}

/**
 * A reading that exists, is drawn, and is not this account's to read yet.
 *
 * The screen behind it is real — the axes, the shape of the tables, the
 * headings — because a reading that were simply absent leaves a customer unable
 * to tell whether we do not measure this or they cannot see it, and only the
 * second has an answer. Blurred rather than filled with invented numbers: a
 * placeholder chart with plausible figures in it is a lie that somebody will
 * eventually screenshot.
 */
export function Gated({
	title,
	body,
	action,
	behind,
}: {
	title: string;
	body: string;
	action: ReactNode;
	behind: ReactNode;
}) {
	return (
		<div className="gated">
			<div className="gated-behind" aria-hidden>
				{behind}
			</div>
			<div className="gated-note">
				<div>
					<h2>{title}</h2>
					<p className="muted">{body}</p>
					{action}
				</div>
			</div>
		</div>
	);
}
